<template>
  <component
    :is="props.to ? FsRouterLink : 'div'"
    :to="to"
    :class="{
      'fs-menu-item': true,
      'fs-menu-item--has-submenu': hasSubMenu,
      'fs-menu-item--disabled': disabled,
    }"
    @mouseover.stop="openSubDropdown"
    @mouseleave="handleMouseLeave"
    @click="handleClick"
  >
    <slot name="icon">
      <span v-if="icon" class="fs-menu-item__icon" v-html="icon"></span>
    </slot>
    <fs-tooltip
      v-if="label"
      :text="label"
      :class="{
        'fs-menu-item__label': true,
        'fs-menu-item__label--small': font === 'small',
        'fs-menu-item__label--checked': checked,
      }"
    >
      {{ label }}
    </fs-tooltip>
    <slot name="default"></slot>
    <span v-if="checked" class="fs-menu-item__checkmark" v-html="checkMarkIcon"></span>
    <span v-if="hasSubMenu" class="fs-menu-item__caret" v-html="caretIcon"></span>
    <fs-popper
      v-if="hasSubMenu"
      ref="popperSubMenuRef"
      placement="right-start"
      :offset="[-7, 8]"
      animation="simple-fade-in-no-out"
      :open="true"
      @open="isSubMenuOpened = true"
      @close="isSubMenuOpened = false"
      @mouseenter="isSubMenuHovered = true"
      @mouseleave="isSubMenuHovered = false"
    >
      <slot name="sub-menu"></slot>
    </fs-popper>
  </component>
</template>

<script setup lang="ts">
import FsPopper from '@components/poppers/fs-popper.vue';
import FsTooltip from '@components/poppers/fs-tooltip.vue';
import caretIcon from '@icons/other/caret-icon.svg';
import checkMarkIcon from '@icons/other/check-mark-icon.svg';
import { computed, ref } from 'vue';
import { waitTimeout } from '@lib/promise';
import { type RouteLocationRaw } from 'vue-router';
import FsRouterLink from '@components/fs-router-link.vue';

const emits = defineEmits<{
  click: [event: MouseEvent];
}>();

const props = withDefaults(
  defineProps<{
    label?: string;
    icon?: string;
    checked?: boolean;
    disabled?: boolean;
    font?: 'standard' | 'small';

    /** Special properties used when using component as a router-link wrapper
     Pass anything to the to attribute and the component will be treated as a router-link wrapper */
    to?: RouteLocationRaw;
  }>(),
  {
    font: 'standard',
    checked: false,
    disabled: false,
  }
);

const slots = defineSlots<{
  icon?: any;
  default?: any;
  'sub-menu'?: any;
}>();

const hasSubMenu = computed(() => slots.hasOwnProperty('sub-menu'));
const isSubMenuOpened = ref(false);
const isSubMenuHovered = ref(false);

const popperSubMenuRef = ref<any | null>(null);

function handleClick(event: MouseEvent) {
  if (props.disabled) {
    event.stopPropagation();
    event.preventDefault();
    return;
  }

  if (hasSubMenu.value) {
    toggleOpenSubDropdown(event);
    event.stopPropagation();
  } else {
    emits('click', event);
  }
}

function toggleOpenSubDropdown(event: MouseEvent) {
  if (isSubMenuOpened.value) {
    closeSubDropdown();
  } else {
    openSubDropdown(event);
  }
}

function openSubDropdown(event: MouseEvent) {
  if (!isSubMenuOpened.value && !props.disabled) {
    popperSubMenuRef.value?.open(event);
  }
}

function closeSubDropdown() {
  if (isSubMenuOpened.value) {
    popperSubMenuRef.value?.close();
  }
}

async function handleMouseLeave() {
  if (isSubMenuOpened.value) {
    // Allow user to navigate to sub menu without closing as soon as cursor leaves parent menu item
    await waitTimeout(70);
    if (!isSubMenuHovered.value) {
      closeSubDropdown();
    }
  }
}
</script>

<style scoped lang="scss">
@import '@styles/variables';

.fs-menu-item {
  padding: 0.5rem 0.8rem;
  position: relative;
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  cursor: pointer;
  // With a `to` this renders an <a>, which without a colour of its own falls back
  // to the browser's link blue — and any artwork drawn with fill="currentColor"
  // picks that up. Setting it here also lets a row recolour as a unit.
  color: $c-dark;

  &:hover {
    background-color: rgba($c-dark, 0.1);
  }

  &--has-submenu {
    cursor: default;
  }

  &--disabled {
    cursor: not-allowed;
    opacity: 0.5;

    &:hover {
      background-color: transparent;
    }
  }

  &__label {
    max-width: 100%;
    flex: 1;
    font-size: 1.4rem;
    line-height: 1.7rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    &--small {
      font-size: 1.2rem;

      //TODO: This should probably be 14px according to design, but then the tooltip always shows.
      line-height: 1.5rem;
    }

    &--checked {
      -webkit-mask-image: linear-gradient(90deg, #ffffff calc(100% - 30px), rgba(255, 255, 255, 0) calc(100% - 15px));
      mask-image: linear-gradient(90deg, #ffffff calc(100% - 30px), rgba(255, 255, 255, 0) calc(100% - 15px));
    }
  }

  &__icon {
    height: 1.4rem;
    width: 1.4rem;
    margin-right: 1.2rem;
    flex-shrink: 0;
    display: block;

    :deep(svg) {
      fill: currentcolor;
      height: 1.4rem;
      width: 1.4rem;
    }
  }

  &__checkmark {
    width: 1.1rem;
    height: 1.4rem;
    position: absolute;
    right: 0.8rem;
    top: calc(50% - 0.7rem);
    display: block;
    fill: currentcolor;
  }

  &__caret {
    width: 0.5rem;
    height: 0.9rem;
    margin-left: 1.2rem;
    flex-shrink: 0;
    display: block;
    fill: currentcolor;
  }
}
</style>
